import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setSelectKitchen } from '../redux/reducers/utils/kitchenReducers/kitchenReducer';

const CHANNEL_NAME = 'kitchen-tab-sync';
const REQUEST_TYPE = 'REQUEST_SELECTED_KITCHEN';
const RESPONSE_TYPE = 'RESPONSE_SELECTED_KITCHEN';

const TAB_ID_KEY = 'kitchenTabId';
const ACTIVE_TAB_KEY = 'activeKitchenTabId';
const INHERIT_PENDING_KEY = 'inheritKitchenPending';
const LAST_SELECTED_KEY = 'lastSelectedKitchen';

const safeParse = (value, label) => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(`Ошибка парсинга ${label}:`, error);
    return null;
  }
};

const getSessionKitchen = () => safeParse(sessionStorage.getItem('selectedKitchen'), 'sessionStorage selectedKitchen');

const getLocalKitchen = () => safeParse(localStorage.getItem(LAST_SELECTED_KEY), 'localStorage lastSelectedKitchen');

const useKitchenTabSync = () => {
  const dispatch = useDispatch();
  const requestIdRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    let tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
      tabId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionStorage.setItem(TAB_ID_KEY, tabId);
    }

    const markActive = () => {
      localStorage.setItem(ACTIVE_TAB_KEY, tabId);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        markActive();
      }
    };

    const channel = 'BroadcastChannel' in window ? new BroadcastChannel(CHANNEL_NAME) : null;

    const handleMessage = (event) => {
      const message = event.data || {};
      const { type, requestId, targetTabId, kitchen } = message;

      if (!type) {
        return;
      }

      if (type === REQUEST_TYPE && targetTabId === tabId) {
        const currentKitchen = getSessionKitchen();
        if (currentKitchen) {
          channel?.postMessage({
            type: RESPONSE_TYPE,
            requestId,
            kitchen: currentKitchen,
          });
        }
        return;
      }

      if (type === RESPONSE_TYPE && requestId === requestIdRef.current) {
        if (!sessionStorage.getItem('selectedKitchen') && kitchen) {
          dispatch(setSelectKitchen(kitchen));
        }
        sessionStorage.removeItem(INHERIT_PENDING_KEY);
      }
    };

    channel?.addEventListener('message', handleMessage);

    const sessionKitchen = getSessionKitchen();
    if (!sessionKitchen) {
      const activeTabId = localStorage.getItem(ACTIVE_TAB_KEY);

      if (channel && activeTabId && activeTabId !== tabId) {
        const requestId = `${tabId}-${Date.now()}`;
        requestIdRef.current = requestId;
        sessionStorage.setItem(INHERIT_PENDING_KEY, '1');
        channel.postMessage({
          type: REQUEST_TYPE,
          requestId,
          targetTabId: activeTabId,
        });

        timeoutRef.current = setTimeout(() => {
          if (!sessionStorage.getItem('selectedKitchen')) {
            const fallbackKitchen = getLocalKitchen();
            if (fallbackKitchen) {
              dispatch(setSelectKitchen(fallbackKitchen));
            }
          }
          sessionStorage.removeItem(INHERIT_PENDING_KEY);
        }, 300);
      } else {
        const fallbackKitchen = getLocalKitchen();
        if (fallbackKitchen) {
          dispatch(setSelectKitchen(fallbackKitchen));
        }
      }
    }

    markActive();
    window.addEventListener('focus', markActive);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      channel?.removeEventListener('message', handleMessage);
      channel?.close();
      window.removeEventListener('focus', markActive);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch]);
};

export default useKitchenTabSync;
